import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Conduit, ConduitInsert, ConduitUpdate, PortProtocol, ChangeLogEntry } from '@/types/conduit';
import type { Database, Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';

type ConduitRowDb = Database['public']['Tables']['conduits']['Row'];
type ConduitInsertDb = Database['public']['Tables']['conduits']['Insert'];
type ConduitUpdateDb = Database['public']['Tables']['conduits']['Update'];

const toJson = (value: unknown): Json => JSON.parse(JSON.stringify(value ?? null)) as Json;

const parsePortsProtocols = (value: Json | null): PortProtocol[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      if (typeof record.port !== 'number' || typeof record.protocol !== 'string') return null;

      const entry: PortProtocol = {
        port: record.port,
        protocol: record.protocol,
      };

      if (typeof record.description === 'string') {
        entry.description = record.description;
      }

      return entry;
    })
    .filter((entry): entry is PortProtocol => entry !== null);
};

const parseChangeLog = (value: Json | null): ChangeLogEntry[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const entries = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const record = item as Record<string, unknown>;
      if (typeof record.timestamp !== 'string' || typeof record.user_id !== 'string' || typeof record.change !== 'string') {
        return null;
      }

      return {
        timestamp: record.timestamp,
        user_id: record.user_id,
        change: record.change,
      } satisfies ChangeLogEntry;
    })
    .filter((entry): entry is ChangeLogEntry => entry !== null);

  return entries.length > 0 ? entries : undefined;
};

const parseConduit = (row: ConduitRowDb): Conduit => {
  const { ports_protocols, change_log, ...rest } = row;

  return {
    ...(rest as Omit<Conduit, 'ports_protocols' | 'change_log'>),
    ports_protocols: parsePortsProtocols(ports_protocols ?? null),
    change_log: parseChangeLog(change_log ?? null),
  };
};

const buildInsertPayload = (
  conduit: ConduitInsert,
  orgId: string,
  userId: string
): ConduitInsertDb => {
  const { ports_protocols, change_log, ...rest } = conduit;

  return {
    ...rest,
    org_id: orgId,
    last_modified_by: userId,
    ports_protocols: toJson(ports_protocols ?? []),
    ...(change_log ? { change_log: toJson(change_log) } : {}),
  } satisfies ConduitInsertDb;
};

const buildUpdatePayload = (
  updates: ConduitUpdate,
  userId?: string
): ConduitUpdateDb => {
  const { ports_protocols, change_log, ...rest } = updates;

  return {
    ...rest,
    ...(userId ? { last_modified_by: userId } : {}),
    ...(typeof ports_protocols !== 'undefined' ? { ports_protocols: toJson(ports_protocols) } : {}),
    ...(typeof change_log !== 'undefined' ? { change_log: toJson(change_log) } : {}),
  } satisfies ConduitUpdateDb;
};

interface ConduitState {
  conduits: Conduit[];
  loading: boolean;

  loadConduits: () => Promise<void>;
  addConduit: (conduit: ConduitInsert) => Promise<Conduit | null>;
  updateConduit: (id: string, updates: ConduitUpdate) => Promise<void>;
  deleteConduit: (id: string) => Promise<void>;
  getConduitById: (id: string) => Conduit | undefined;
  getConduitsByZone: (zoneId: string) => Conduit[];
}

export const useConduitStore = create<ConduitState>()((set, get) => ({
  conduits: [],
  loading: false,

  loadConduits: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('conduits')
        .select('*')
        .order('name');

      if (error) throw error;
      const conduits = (data ?? []).map((row) => parseConduit(row));
      set({ conduits });
    } catch (error) {
      console.error('Error loading conduits:', error);
      toast.error('Failed to load conduits');
    } finally {
      set({ loading: false });
    }
  },

  addConduit: async (conduit) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .single();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      if (conduit.src_zone_id === conduit.dst_zone_id) {
        toast.error('Source and destination zones must be different');
        return null;
      }

      const insertPayload = buildInsertPayload(conduit, profile.org_id, userData.user.id);

      const { data, error } = await supabase
        .from('conduits')
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;
      if (!data) throw new Error('Conduit creation returned no data');

      const createdConduit = parseConduit(data);
      set(state => ({ conduits: [...state.conduits, createdConduit] }));
      toast.success('Conduit created');
      return createdConduit;
    } catch (error) {
      console.error('Error creating conduit:', error);
      toast.error('Failed to create conduit');
      return null;
    }
  },

  updateConduit: async (id, updates) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const updatePayload = buildUpdatePayload(updates, userData.user?.id ?? undefined);

      const { error } = await supabase
        .from('conduits')
        .update(updatePayload)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        conduits: state.conduits.map(c => c.id === id ? { ...c, ...updates } : c)
      }));
      toast.success('Conduit updated');
    } catch (error) {
      console.error('Error updating conduit:', error);
      toast.error('Failed to update conduit');
    }
  },

  deleteConduit: async (id) => {
    try {
      const { error } = await supabase
        .from('conduits')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        conduits: state.conduits.filter(c => c.id !== id)
      }));
      toast.success('Conduit deleted');
    } catch (error) {
      console.error('Error deleting conduit:', error);
      toast.error('Failed to delete conduit');
    }
  },

  getConduitById: (id) => {
    return get().conduits.find(c => c.id === id);
  },

  getConduitsByZone: (zoneId) => {
    return get().conduits.filter(c => 
      c.src_zone_id === zoneId || c.dst_zone_id === zoneId
    );
  },
}));
